import { humanizeEnum } from '../../utils/format';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { RoleService } from '../../services/role.service';
import { IRole, IRoleTemplate } from '../../interfaces/role.interface';
import TemplatePickerDialog from './TemplatePickerDialog';
import GroupAccessDialog from '../institutions/GroupAccessDialog';
import { InstitutionGroupService } from '../../services/institutionGroup.service';

const RolesPage: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();

    const [roles, setRoles] = useState<IRole[]>([]);
    const [loading, setLoading] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);
    // polish 6: shown only when this institution belongs to an institution group
    const [inGroup, setInGroup] = useState(false);
    const [groupAccessOpen, setGroupAccessOpen] = useState(false);

    const load = useCallback(async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const data = await RoleService.list(selectedInstitutionId);
            setRoles(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load roles');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        if (!selectedInstitutionId) return;
        InstitutionGroupService.mine(selectedInstitutionId).then(g => setInGroup(!!g)).catch(() => setInGroup(false));
    }, [selectedInstitutionId]);

    const handleToggleActive = async (role: IRole) => {
        if (!selectedInstitutionId) return;
        try {
            await RoleService.setActive(selectedInstitutionId, role.id, !role.isActive);
            await load();
            toast.showSuccess(role.isActive ? 'Role deactivated' : 'Role activated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update role');
        }
    };

    const handleDelete = async (role: IRole) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(`Delete the role "${role.name}"? This cannot be undone.`)) return;
        try {
            await RoleService.remove(selectedInstitutionId, role.id);
            await load();
            toast.showSuccess('Role deleted');
        } catch (err) {
            toast.showApiError(err, 'Failed to delete role');
        }
    };

    const handlePick = (template: IRoleTemplate | null) => {
        setPickerOpen(false);
        navigate(template ? `/roles/new?template=${template.key}` : '/roles/new');
    };

    return (
        <Box sx={{ padding: 3 }} data-help-page="roles.list.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Roles</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {inGroup && <Button variant="outlined" onClick={() => setGroupAccessOpen(true)} data-help="roles.list.groupMembers">Members from group companies</Button>}
                    <Button variant="contained" onClick={() => setPickerOpen(true)} data-help="roles.list.new">New role</Button>
                </Box>
            </Box>
            {/* W3-K: auditor role help */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="auditor-role-help">
                Need an auditor? Create a role from the "Auditor" template: it gives read-only access to the books,
                financial reports, source documents and the audit log, and no create, edit, post, approve or pay
                permissions. Payroll stays hidden unless you add the payroll view permission to that role.
            </Typography>
            <TableContainer component={Paper} data-help-section="roles.list.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="roles.list.col.name">Name</TableCell>
                            <TableCell data-help="roles.list.col.scope">Scope</TableCell>
                            <TableCell data-help="roles.list.col.members">Members</TableCell>
                            <TableCell data-help="roles.list.col.active">Active</TableCell>
                            <TableCell align="right" data-help="roles.list.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && roles.map(role => (
                            <TableRow key={role.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">{role.name}</Typography>
                                        {role.isAdmin && <Chip size="small" color="warning" label="Locked" data-help="roles.list.locked" />}
                                        {!role.isAdmin && role.templateKey && <Chip size="small" variant="outlined" label="Suggested" data-help="roles.list.suggested" />}
                                    </Box>
                                    {role.description && (
                                        <Typography variant="caption" color="text.secondary">{role.description}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>{humanizeEnum(role.scope)}</TableCell>
                                <TableCell>{role.memberCount}</TableCell>
                                <TableCell>{role.isActive ? 'Active' : 'Inactive'}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View / Edit">
                                        <IconButton aria-label="View role" data-help="roles.list.action.edit" size="small" onClick={() => navigate(`/roles/${role.id}`)}>
                                            {role.isAdmin ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                    {!role.isAdmin && (
                                        <Tooltip title={role.isActive ? 'Deactivate' : 'Activate'}>
                                            <IconButton aria-label={role.isActive ? 'Deactivate role' : 'Activate role'} data-help="roles.list.action.toggle" size="small" onClick={() => handleToggleActive(role)}>
                                                {role.isActive ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!role.isAdmin && role.memberCount === 0 && (
                                        <Tooltip title="Delete">
                                            <IconButton aria-label="Delete role" data-help="roles.list.action.delete" size="small" onClick={() => handleDelete(role)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {selectedInstitutionId && (
                <GroupAccessDialog open={groupAccessOpen} onClose={() => { setGroupAccessOpen(false); void load(); }}
                    institutionId={selectedInstitutionId} />
            )}
            <TemplatePickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={handlePick}
                usedTemplateKeys={roles.map(r => r.templateKey).filter((k): k is string => !!k)} />
        </Box>
    );
};

export default RolesPage;
